import React, { useState, useEffect, useCallback, useRef } from 'react'
import { get, noop, reduce, floor, omit, once, delay } from 'lodash'
import { useDispatch } from 'react-redux'
import useInterval from '../../hooks/useInterval'
import { useParams } from 'react-router-dom'
import { useAddViewToVideoMutation } from '../../store/api'
import { openModal } from '../../store/modal'
import { useContext } from 'react'
import YoutubeAPIContext from './context'
import useContainerSize from '../../hooks/useContainerSize'

const YOUTUBE_CONTAINER = 'YOUTUBE_CONTAINER'
const calcHeightRation = width => (9 * width) / 16

const preparePlayer = ({ onVideoStateChange, width }) =>
  new Promise(resolve => {
    const height = calcHeightRation(width)

    new window.YT.Player(YOUTUBE_CONTAINER, {
      height,
      width,
      playerVars: {
        playsinline: 1,
        controls: 1,
        autoplay: 0,
        disablekb: 1,
        modestbranding: 1
      },
      events: {
        onReady: ({ target }) => resolve(target),
        onStateChange: onVideoStateChange
      }
    })
  })

const initialTickerCallback = () => () => noop

const initalQuestionTicker = { callbackCreator: initialTickerCallback, delay: null }

const videoEnded = playerAPI => playerAPI.getPlayerState() === window?.YT?.PlayerState.ENDED

function YoutubeQuestioner({ youtubeId, questions }) {
  const ref = useRef(null)
  const context = useContext(YoutubeAPIContext)
  const dispatch = useDispatch()
  const [questionsTicker, setQuestionsTicker] = useState(initalQuestionTicker)
  const [questionsByTime, setQuestionsByTime] = useState({})
  const [addViewToVideo] = useAddViewToVideoMutation()
  const preparePlayerOnce = React.useRef(once(preparePlayer)).current
  const { id } = useParams()
  const { width } = useContainerSize(ref)

  useInterval(questionsTicker.callbackCreator(questionsByTime, id), questionsTicker.delay)

  useEffect(() => context.resetPlayer, [context.resetPlayer])

  useEffect(() => {
    if (context.player.loaded && width) {
      context.player.api.setSize(width, calcHeightRation(width))
    }
  }, [width, context.player.loaded, context.player.api])

  const openQuestionModal = useCallback(question => dispatch(openModal(question)), [dispatch])

  const startQuestionsTicker = useCallback(
    playerAPI => {
      const tickerCallback = (questions, id) => () => {
        let done = false
        const videoTime = floor(playerAPI.getCurrentTime())
        const question = get(questions, videoTime)

        if (question) {
          delay(() => {
            playerAPI.pauseVideo()
          }, 100)

          openQuestionModal(question)

          const withoutCurrentQuestion = omit(questions, videoTime)
          setQuestionsByTime(withoutCurrentQuestion)

          delay(() => {
            if (!videoEnded(playerAPI)) {
              playerAPI.playVideo()
            }
          }, 1)
        }

        delay(() => {
          if (videoEnded(playerAPI) && !done) {
            done = true

            addViewToVideo(id)
          }
        }, 300)
      }

      const activeSearcherTicker = {
        callbackCreator: tickerCallback,
        delay: 300
      }

      setQuestionsTicker(activeSearcherTicker)
    },
    [addViewToVideo, openQuestionModal]
  )

  const stopQuestionsTicker = useCallback(() => {
    setQuestionsTicker(initalQuestionTicker)
  }, [])

  const onVideoStateChange = useCallback(
    ({ data, target: playerAPI }) => {
      const playerStates = window?.YT?.PlayerState

      switch (data) {
        case playerStates?.ENDED:
          stopQuestionsTicker()
          break
        case playerStates?.PAUSED:
          stopQuestionsTicker()
          break
        case playerStates?.PLAYING:
          startQuestionsTicker(playerAPI)
          break
        default:
          break
      }
    },
    [startQuestionsTicker, stopQuestionsTicker]
  )

  useEffect(() => {
    if (width && !context.player.loaded) {
      preparePlayerOnce({ onVideoStateChange, width }).then(api => {
        context.setPlayer({ api, loaded: true })
      })
    }
  }, [onVideoStateChange, context.player.loaded, preparePlayerOnce, width, id, context])

  useEffect(() => {
    if (context.player.loaded) {
      const normalizedQuestions = reduce(
        questions,
        (accumulator, question) => ({ ...accumulator, [question.time]: question }),
        {}
      )
      setQuestionsByTime(normalizedQuestions)
      context.player.api.loadVideoById(youtubeId).stopVideo()
    }
  }, [context.player, youtubeId, questions])

  return (
    <div ref={ref}>
      <div id={YOUTUBE_CONTAINER} />
    </div>
  )
}

export default YoutubeQuestioner
